/**
 * Map common email domains to webmail inbox deep links.
 *
 * Used by the post-magic-link "Check your email" state on LoginPage to
 * show a one-click "Open Gmail" / "Open Outlook" button instead of
 * making teachers tab-switch and hunt for the email manually. Same
 * pattern Slack, Linear, Notion use on their signin flows.
 *
 * For Gmail we use a search URL pre-filtered to "TeacherDashboard" so
 * the magic-link message floats to the top regardless of inbox state
 * (promotions tab, social tab, etc.). Other providers don't have
 * portable search URLs so we just deep-link to the inbox.
 *
 * If the domain isn't in the table we return null — the UI then shows
 * the generic "Check your email" copy without an open-inbox button.
 * School/district domains (peelschools.org, tdsb.on.ca, etc.) fall
 * into this bucket since they're usually webmail-via-OWA or M365 on
 * a per-tenant subdomain we can't predict.
 */

export interface EmailProvider {
  /** Display label shown on the button, e.g. "Gmail". */
  name: string;
  /** Direct link the button opens in a new tab. */
  inboxUrl: string;
}

// Gmail's search URL supports inline filters. Pre-filtering to
// "TeacherDashboard" surfaces the magic link even when it lands in the
// Promotions tab or a busy inbox. The encoded "#search/<term>" is the
// hash-fragment-route Gmail uses to drive the search input on load.
const GMAIL_INBOX = "https://mail.google.com/mail/u/0/#search/TeacherDashboard";

const PROVIDERS: Record<string, EmailProvider> = {
  "gmail.com": { name: "Gmail", inboxUrl: GMAIL_INBOX },
  "googlemail.com": { name: "Gmail", inboxUrl: GMAIL_INBOX },
  // Outlook/Hotmail/Live all redirect into outlook.live.com for personal
  // accounts. Corporate M365 tenants land at outlook.office.com, which
  // we can't deep-link to without knowing the tenant.
  "outlook.com": { name: "Outlook", inboxUrl: "https://outlook.live.com/mail/0/inbox" },
  "hotmail.com": { name: "Outlook", inboxUrl: "https://outlook.live.com/mail/0/inbox" },
  "live.com": { name: "Outlook", inboxUrl: "https://outlook.live.com/mail/0/inbox" },
  "msn.com": { name: "Outlook", inboxUrl: "https://outlook.live.com/mail/0/inbox" },
  "yahoo.com": { name: "Yahoo", inboxUrl: "https://mail.yahoo.com/" },
  "ymail.com": { name: "Yahoo", inboxUrl: "https://mail.yahoo.com/" },
  "rocketmail.com": { name: "Yahoo", inboxUrl: "https://mail.yahoo.com/" },
  "icloud.com": { name: "iCloud", inboxUrl: "https://www.icloud.com/mail" },
  "me.com": { name: "iCloud", inboxUrl: "https://www.icloud.com/mail" },
  "mac.com": { name: "iCloud", inboxUrl: "https://www.icloud.com/mail" },
  "aol.com": { name: "AOL", inboxUrl: "https://mail.aol.com/" },
  "proton.me": { name: "Proton", inboxUrl: "https://mail.proton.me/u/0/inbox" },
  "protonmail.com": { name: "Proton", inboxUrl: "https://mail.proton.me/u/0/inbox" },
  "pm.me": { name: "Proton", inboxUrl: "https://mail.proton.me/u/0/inbox" },
  "fastmail.com": { name: "Fastmail", inboxUrl: "https://app.fastmail.com/mail/Inbox" },
  "zoho.com": { name: "Zoho", inboxUrl: "https://mail.zoho.com/" },
};

/**
 * Look up the webmail provider for an email address.
 * Returns null if the domain isn't recognized (school/work domains
 * usually fall through). Defensive against malformed input.
 */
export function getInboxProvider(email: string): EmailProvider | null {
  if (typeof email !== "string") return null;
  const at = email.lastIndexOf("@");
  if (at === -1 || at === email.length - 1) return null;
  const domain = email.slice(at + 1).toLowerCase().trim();
  return PROVIDERS[domain] ?? null;
}
